if (!customElements.get('cart-popup')) {
  customElements.define(
    'cart-popup',
    class CartPopup extends HTMLElement {
      constructor() {
        super();

        this.activeElement = null;
        this.quickViewTrigger = null;
        this.quickViewSwiper = null;
        this.cartMutationLocked = false;
        this.cartMutationTimer = null;
        this.cartMutationStartedAt = 0;
        this.cartMutationCooldown = 2500;
        this.cartMutationBackoff = 8000;
        this.cartNormalizationPromise = null;
        this.addEventListener('click', this.onClick.bind(this));
        this.addEventListener('change', this.onChange.bind(this));
        this.addEventListener('keyup', (event) => {
          if (event.code !== 'Escape') return;
          if (this.classList.contains('is-quickview-open')) {
            this.closeQuickView();
          } else {
            this.close();
          }
        });
        if (document.readyState === 'loading') {
          document.addEventListener('DOMContentLoaded', () => this.setHeaderCartIconAccessibility(), { once: true });
        } else {
          this.setHeaderCartIconAccessibility();
        }
        document.addEventListener('shopify:section:load', () => this.setHeaderCartIconAccessibility());
      }

      connectedCallback() {
      }

      setHeaderCartIconAccessibility() {
        const cartLink = document.querySelector('#cart-icon-bubble');
        if (!cartLink) return;
        if (cartLink.dataset.cartPopupBound === 'true') return;

        cartLink.dataset.cartPopupBound = 'true';
        cartLink.setAttribute('role', 'button');
        cartLink.setAttribute('aria-haspopup', 'dialog');
        cartLink.addEventListener('click', (event) => {
          event.preventDefault();
          this.open(cartLink);
        });
        cartLink.addEventListener('keydown', (event) => {
          if (event.code.toUpperCase() === 'SPACE') {
            event.preventDefault();
            this.open(cartLink);
          }
        });
      }

      onClick(event) {
        const recommendationsToggle = event.target.closest('[data-cart-recommendations-toggle]');
        if (recommendationsToggle && this.contains(recommendationsToggle)) {
          event.preventDefault();
          this.toggleRecommendations(recommendationsToggle);
          return;
        }

        const quickViewClose = event.target.closest('[data-cart-quickview-close]');
        if (quickViewClose && this.contains(quickViewClose)) {
          event.preventDefault();
          this.closeQuickView();
          return;
        }

        const quickViewOpen = event.target.closest('[data-cart-recommendation-quickview-open]');
        if (quickViewOpen && this.contains(quickViewOpen)) {
          event.preventDefault();
          this.openQuickView(quickViewOpen);
          return;
        }

        const quickViewSwatch = event.target.closest('[data-cart-quickview-option-swatch]');
        if (quickViewSwatch && this.contains(quickViewSwatch)) {
          event.preventDefault();
          this.selectQuickViewSwatch(quickViewSwatch);
          return;
        }

        const quickViewAdd = event.target.closest('[data-cart-quickview-add]');
        if (quickViewAdd && this.contains(quickViewAdd)) {
          event.preventDefault();
          this.addQuickViewToCart(quickViewAdd);
          return;
        }

        const checkoutButton = event.target.closest('[data-cart-popup-checkout]');
        if (checkoutButton && this.contains(checkoutButton)) {
          event.preventDefault();
          this.goToCheckout();
          return;
        }

        if (event.target.closest('[data-cart-popup-close]')) {
          event.preventDefault();
          this.close();
          return;
        }

        const quantityButton = event.target.closest('[data-cart-popup-quantity]');
        if (quantityButton && this.contains(quantityButton)) {
          event.preventDefault();
          this.updateLine(quantityButton);
          return;
        }

        const removeButton = event.target.closest('[data-cart-popup-remove]');
        if (!removeButton || !this.contains(removeButton)) return;

        event.preventDefault();
        this.updateLine(removeButton, 0);
      }

      onChange(event) {
        const select = event.target.closest('[data-cart-quickview-option-select]');
        if (!select || !this.contains(select)) return;

        this.updateQuickViewProduct(select.closest('[data-cart-quickview-product]'));
      }

      toggleRecommendations(button) {
        const section = button.closest('[data-cart-popup-recommendations]');
        const list = section?.querySelector('[data-cart-recommendations-list]');
        const icon = button.querySelector('[data-cart-recommendations-toggle-icon]');
        if (!section || !list) return;

        const isExpanded = button.getAttribute('aria-expanded') !== 'false';
        button.setAttribute('aria-expanded', isExpanded ? 'false' : 'true');
        list.hidden = isExpanded;
        section.classList.toggle('is-collapsed', isExpanded);
        if (icon) icon.textContent = isExpanded ? '+' : '-';
      }

      open(triggeredBy) {
        if (triggeredBy) this.setActiveElement(triggeredBy);

        this.ensureFooterActions();
        this.classList.add('is-open');
      }

      close() {
        this.closeQuickView({ restoreFocus: false });
        this.classList.remove('is-open');

        if (this.activeElement) {
          this.activeElement.focus();
        }
      }

      renderContents(parsedState, shouldOpen = true) {
        if (!parsedState?.sections) {
          this.refresh().then(() => this.open());
          return;
        }

        this.closeQuickView({ restoreFocus: false });

        this.getSectionsToRender().forEach((section) => {
          const sectionElement = section.selector
            ? document.querySelector(section.selector)
            : document.getElementById(section.id);

          if (!sectionElement || !parsedState.sections[section.id]) return;
          const sectionInnerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
          if (sectionInnerHTML === null) return;
          sectionElement.innerHTML = sectionInnerHTML;
        });

        this.classList.toggle('is-empty', Boolean(this.querySelector('.cart-popup__empty')));
        if (this.cartMutationLocked) this.setCartMutationControlsLocked(true);
        this.ensureFooterActions();
        if (shouldOpen) this.open();
      }

      async refresh(shouldOpen = true, normalizeDuplicates = true) {
        if (normalizeDuplicates) {
          try {
            const normalizedState = await this.normalizeDuplicateLines();
            if (normalizedState?.sections) {
              this.renderContents(normalizedState, shouldOpen);
              return normalizedState;
            }
          } catch (error) {
            console.error(error);
          }
        }

        const sectionIds = this.getSectionsToRender().map((section) => section.id).join(',');
        const response = await fetch(`${routes.cart_url}?sections=${sectionIds}&sections_url=${window.location.pathname}`);
        const sections = await this.parseJsonResponse(response);
        const parsedState = { sections };
        this.renderContents(parsedState, shouldOpen);
        return parsedState;
      }

      async renderNormalizedContents(parsedState, shouldOpen = true) {
        let stateToRender = parsedState;

        try {
          const normalizedState = await this.normalizeDuplicateLines();
          if (normalizedState?.sections) stateToRender = normalizedState;
        } catch (error) {
          console.error(error);
        }

        if (stateToRender?.sections) {
          this.renderContents(stateToRender, shouldOpen);
          return true;
        }

        await this.refresh(shouldOpen, false);
        return true;
      }

      parseJsonResponse(response) {
        return response.text().then((text) => {
          try {
            return JSON.parse(text);
          } catch (error) {
            if (response.status === 429) {
              return {
                status: 429,
                description: 'Please wait a moment before updating your cart.',
              };
            }

            throw new Error(window.cartStrings?.error || 'Unable to update cart.');
          }
        });
      }

      getCartChangeUrl() {
        const cartChangeUrl = window.routes && window.routes.cart_change_url ? window.routes.cart_change_url : '/cart/change';
        return cartChangeUrl.endsWith('.js') ? cartChangeUrl : `${cartChangeUrl}.js`;
      }

      getCartUpdateUrl() {
        const cartUpdateUrl = window.routes && window.routes.cart_update_url ? window.routes.cart_update_url : '/cart/update';
        return cartUpdateUrl.endsWith('.js') ? cartUpdateUrl : `${cartUpdateUrl}.js`;
      }

      getCartJsonUrl() {
        const cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : '/cart';
        const cartPath = String(cartUrl).split('?')[0].replace(/\/$/, '');
        return cartPath.endsWith('.js') ? cartPath : `${cartPath}.js`;
      }

      isBlankXcottonPlanValue(value) {
        return value === '';
      }

      getXcottonPlanMarkerState(item) {
        const properties = item?.properties;
        if (!properties || !Object.prototype.hasOwnProperty.call(properties, '_PlanID')) return 'absent';
        return this.isBlankXcottonPlanValue(properties._PlanID) ? 'blank' : 'selected';
      }

      stableSerialize(value) {
        if (Array.isArray(value)) {
          return `[${value.map((entry) => this.stableSerialize(entry)).join(',')}]`;
        }

        if (value && typeof value === 'object') {
          return `{${Object.keys(value)
            .sort()
            .map((key) => `${JSON.stringify(key)}:${this.stableSerialize(value[key])}`)
            .join(',')}}`;
        }

        if (value === undefined) return 'undefined';
        return JSON.stringify(value);
      }

      getLineMergeSignature(item) {
        const properties = { ...(item?.properties || {}) };
        if (Object.prototype.hasOwnProperty.call(properties, '_PlanID') && this.isBlankXcottonPlanValue(properties._PlanID)) {
          delete properties._PlanID;
        }

        return this.stableSerialize({
          variantId: String(item?.variant_id ?? item?.id ?? ''),
          productId: String(item?.product_id ?? ''),
          properties,
          sellingPlanAllocation: item?.selling_plan_allocation || null,
          price: item?.price ?? null,
          originalPrice: item?.original_price ?? null,
          discountedPrice: item?.discounted_price ?? null,
          finalPrice: item?.final_price ?? null,
          discounts: item?.discounts || [],
          discountAllocations: item?.line_level_discount_allocations || [],
        });
      }

      getDuplicateLineUpdates(cartState) {
        if (!Array.isArray(cartState?.items)) return {};

        // Xcotton marks an unprotected, eligible item with `_PlanID: ''`. If its
        // add interceptor has not loaded yet, the same variant is added without
        // that marker and Shopify creates a second line. Only reconcile that one
        // exact missing-vs-empty difference; every other line distinction stays.
        const groups = new Map();

        cartState.items.forEach((item) => {
          const markerState = this.getXcottonPlanMarkerState(item);
          const quantity = Number(item?.quantity);
          const hasComponents = Array.isArray(item?.item_components) && item.item_components.length > 0;
          const hasParentRelationship = Boolean(item?.parent_relationship || item?.parent_relationships);
          const cannotUpdateQuantity = item?.instructions?.can_update_quantity === false;
          const cannotRemove = item?.instructions?.can_remove === false;
          if (
            markerState === 'selected' ||
            item?.gift_card ||
            item?.remote ||
            hasComponents ||
            hasParentRelationship ||
            cannotUpdateQuantity ||
            cannotRemove ||
            !item?.key ||
            !Number.isFinite(quantity) ||
            quantity <= 0
          ) return;

          const signature = this.getLineMergeSignature(item);
          const group = groups.get(signature) || [];
          group.push({ item, markerState, quantity });
          groups.set(signature, group);
        });

        const updates = {};

        groups.forEach((group) => {
          const blankMarkerItems = group.filter((entry) => entry.markerState === 'blank');
          const absentMarkerItems = group.filter((entry) => entry.markerState === 'absent');
          if (!blankMarkerItems.length || !absentMarkerItems.length) return;

          const canonical = blankMarkerItems[0];
          updates[canonical.item.key] = group.reduce((total, entry) => total + entry.quantity, 0);

          group.forEach((entry) => {
            if (entry !== canonical) updates[entry.item.key] = 0;
          });
        });

        return updates;
      }

      async normalizeDuplicateLines() {
        if (this.cartNormalizationPromise) return this.cartNormalizationPromise;

        this.cartNormalizationPromise = (async () => {
          const cartResponse = await fetch(`${this.getCartJsonUrl()}?_=${Date.now()}`, {
            cache: 'no-store',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest',
            },
          });
          const cartState = await this.parseJsonResponse(cartResponse);

          if (!cartResponse.ok || cartState.status) {
            throw this.getCartError(cartState, cartResponse, 'Unable to inspect cart.');
          }

          const updates = this.getDuplicateLineUpdates(cartState);
          if (!Object.keys(updates).length) return null;

          const body = JSON.stringify({
            updates,
            sections: this.getSectionsToRender().map((section) => section.id),
            sections_url: window.location.pathname,
          });
          const config = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  Accept: 'application/json',
                  'Content-Type': 'application/json',
                },
              };
          config.body = body;

          return this.fetchCartMutation(
            this.getCartUpdateUrl(),
            config,
            'Unable to merge duplicate cart items.'
          );
        })();

        try {
          return await this.cartNormalizationPromise;
        } finally {
          this.cartNormalizationPromise = null;
        }
      }

      getCartAddUrl() {
        return window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : '/cart/add';
      }

      getCartMutationControls() {
        return this.querySelectorAll(
          '[data-cart-popup-quantity], [data-cart-popup-remove], [data-cart-quickview-add], [data-cart-popup-checkout]'
        );
      }

      setCartMutationControlsLocked(isLocked) {
        this.getCartMutationControls().forEach((control) => {
          if (isLocked) {
            if (control.disabled) {
              control.dataset.cartPopupWasDisabled = 'true';
            } else {
              delete control.dataset.cartPopupWasDisabled;
            }

            control.dataset.cartPopupLocked = 'true';
            control.disabled = true;
            control.setAttribute('aria-disabled', 'true');
            return;
          }

          if (control.dataset.cartPopupLocked !== 'true') return;

          const shouldStayDisabled = control.dataset.cartPopupWasDisabled === 'true' || control.dataset.available === 'false';
          control.disabled = shouldStayDisabled;
          control.removeAttribute('aria-disabled');
          delete control.dataset.cartPopupLocked;
          delete control.dataset.cartPopupWasDisabled;
        });
      }

      setCartMutationLoading(controls, isLoading) {
        controls.forEach((control) => {
          if (!control || !document.contains(control)) return;

          if (control.matches('[data-cart-quickview-add]')) {
            this.setQuickViewButtonLoading(control, isLoading);
            return;
          }

          control.classList.toggle('is-loading', isLoading);
        });
      }

      startCartMutation(activeControls) {
        if (this.cartMutationLocked) return false;

        window.clearTimeout(this.cartMutationTimer);
        this.cartMutationLocked = true;
        this.cartMutationStartedAt = Date.now();
        this.setCartMutationLoading(activeControls, true);
        this.setCartMutationControlsLocked(true);

        return true;
      }

      finishCartMutation(activeControls, cooldown = this.cartMutationCooldown, cooldownFromNow = false) {
        const elapsed = Date.now() - this.cartMutationStartedAt;
        // A 429 cooldown starts when the rate-limit response is received. Measuring
        // it from the original click can unlock the controls immediately after a
        // delayed response and cause another request to hit the same protection.
        const delay = cooldownFromNow ? cooldown : Math.max(cooldown - elapsed, 0);

        this.cartMutationTimer = window.setTimeout(() => {
          this.setCartMutationLoading(activeControls, false);
          this.cartMutationLocked = false;
          this.setCartMutationControlsLocked(false);
          this.ensureFooterActions();
        }, delay);
      }

      ensureFooterActions() {
        const footer = this.querySelector('.cart-popup__footer');
        if (!footer) return null;

        let actions = footer.querySelector('.cart-popup__actions');
        if (!actions) {
          actions = document.createElement('div');
          actions.className = 'cart-popup__actions';
          footer.appendChild(actions);
        }

        let checkoutButton = actions.querySelector('[data-cart-popup-checkout]');
        if (!checkoutButton) {
          checkoutButton = document.createElement('button');
          checkoutButton.className = 'cart-popup__checkout';
          checkoutButton.type = 'button';
          checkoutButton.setAttribute('data-cart-popup-checkout', '');
          actions.prepend(checkoutButton);
        }

        checkoutButton.type = 'button';
        checkoutButton.removeAttribute('name');
        checkoutButton.removeAttribute('hidden');
        checkoutButton.removeAttribute('aria-hidden');
        checkoutButton.classList.remove('hidden');
        if (!checkoutButton.textContent.trim()) {
          checkoutButton.textContent = 'Secure Checkout';
        }

        [actions, checkoutButton].forEach((element) => {
          element.style.removeProperty('display');
          element.style.removeProperty('visibility');
          element.style.removeProperty('opacity');
        });

        if (!this.cartMutationLocked && checkoutButton.dataset.cartPopupLocked !== 'true') {
          checkoutButton.disabled = false;
          checkoutButton.removeAttribute('aria-disabled');
        }

        if (actions.firstElementChild !== checkoutButton) {
          actions.prepend(checkoutButton);
        }

        return checkoutButton;
      }

      goToCheckout() {
        if (this.cartMutationLocked) return;

        window.location.href = this.getCheckoutUrl();
      }

      getCheckoutUrl() {
        const cartUrl = window.routes?.cart_url || '/cart';
        const cartPath = String(cartUrl).split('?')[0].replace(/\/$/, '');

        if (cartPath.endsWith('/cart')) {
          return `${cartPath.slice(0, -5)}/checkout`;
        }

        return '/checkout';
      }

      getRetryDelay(response) {
        const retryAfter = response.headers?.get?.('Retry-After');
        const retryAfterSeconds = Number(retryAfter);
        if (Number.isFinite(retryAfterSeconds) && retryAfterSeconds > 0) return retryAfterSeconds * 1000;

        const retryAfterDate = Date.parse(retryAfter || '');
        if (Number.isFinite(retryAfterDate) && retryAfterDate > Date.now()) return retryAfterDate - Date.now();

        return this.cartMutationBackoff;
      }

      getCartError(parsedState, response, fallbackMessage = 'Unable to update cart.') {
        const error = new Error(parsedState.description || parsedState.message || window.cartStrings?.error || fallbackMessage);
        error.status = Number(parsedState.status || response.status);

        if (error.status === 429) error.retryAfter = this.getRetryDelay(response);

        return error;
      }

      async fetchCartMutation(url, config, fallbackMessage) {
        const response = await fetch(url, config);
        const parsedState = await this.parseJsonResponse(response);

        if (!response.ok || parsedState.status) {
          throw this.getCartError(parsedState, response, fallbackMessage);
        }

        return parsedState;
      }

      async updateLine(button, quantityOverride) {
        const line = Number(button.dataset.line);
        const key = button.dataset.key;
        const quantity = Number(quantityOverride ?? button.dataset.quantity);
        if ((!key && !line) || !Number.isFinite(quantity) || button.getAttribute('aria-disabled') === 'true') return;

        const item = button.closest('.cart-popup-item');
        const activeControls = Array.from(item ? item.querySelectorAll('[data-cart-popup-quantity], [data-cart-popup-remove]') : [button]);
        if (!this.startCartMutation(activeControls)) return;

        const body = JSON.stringify({
          ...(key ? { id: key } : { line }),
          quantity: Math.max(quantity, 0),
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        });
        let mutationCooldown = this.cartMutationCooldown;
        let cooldownFromNow = false;

        try {
          const parsedState = await this.fetchCartMutation(this.getCartChangeUrl(), {
            ...fetchConfig(),
            body,
          }, 'Unable to update cart.');

          if (parsedState.sections) {
            this.renderContents(parsedState, this.classList.contains('is-open'));
          } else {
            await this.refresh();
          }

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-popup',
                cartData: parsedState,
              });
            } catch (publishError) {
              console.error(publishError);
            }
          }
        } catch (error) {
          const status = this.querySelector('[data-cart-popup-status]');
          if (status) status.textContent = error.message || window.cartStrings?.error || 'Unable to update cart.';
          mutationCooldown = error.status === 429 ? error.retryAfter || this.cartMutationBackoff : this.cartMutationCooldown;
          cooldownFromNow = error.status === 429;
        } finally {
          if (this.cartMutationLocked) this.finishCartMutation(activeControls, mutationCooldown, cooldownFromNow);
        }
      }

      openQuickView(button) {
        const recommendation = button.closest('[data-cart-recommendation]');
        const template = recommendation?.querySelector('[data-cart-recommendation-template]');
        const panel = this.querySelector('[data-cart-quickview]');
        const content = this.querySelector('[data-cart-quickview-content]');

        if (!template || !panel || !content) return;

        this.closeQuickView({ restoreFocus: false });
        this.quickViewTrigger = button;
        content.innerHTML = '';
        content.appendChild(template.content.cloneNode(true));
        panel.hidden = false;
        this.classList.add('is-quickview-open');

        this.updateQuickViewProduct(content.querySelector('[data-cart-quickview-product]'));
        this.initQuickViewSwiper(panel);

        const closeButton = panel.querySelector('[data-cart-quickview-close]');
        if (closeButton) closeButton.focus();
      }

      closeQuickView(options = {}) {
        const { restoreFocus = true } = options;
        const panel = this.querySelector('[data-cart-quickview]');
        const content = this.querySelector('[data-cart-quickview-content]');

        this.destroyQuickViewSwiper();
        this.classList.remove('is-quickview-open');
        if (content) content.innerHTML = '';
        if (panel) panel.hidden = true;

        if (restoreFocus && this.quickViewTrigger && document.contains(this.quickViewTrigger)) {
          this.quickViewTrigger.focus();
        }
        this.quickViewTrigger = null;
      }

      initQuickViewSwiper(panel, attempt = 0) {
        const swiperEl = panel?.querySelector('[data-cart-quickview-swiper]');
        if (!swiperEl || swiperEl.swiper) return;

        if (!window.Swiper) {
          if (attempt < 20 && !panel.hidden) {
            window.setTimeout(() => this.initQuickViewSwiper(panel, attempt + 1), 100);
          }
          return;
        }

        this.quickViewSwiper = new Swiper(swiperEl, {
          slidesPerView: 1,
          spaceBetween: 0,
          watchOverflow: true,
          pagination: {
            el: panel.querySelector('[data-cart-quickview-pagination]'),
            clickable: true,
          },
        });
      }

      destroyQuickViewSwiper() {
        if (this.quickViewSwiper && !this.quickViewSwiper.destroyed) {
          this.quickViewSwiper.destroy(true, true);
        }
        this.quickViewSwiper = null;
      }

      getQuickViewVariants(product) {
        if (!product) return [];
        if (product.quickViewVariants) return product.quickViewVariants;

        const variantData = product.querySelector('[data-cart-quickview-variants]');
        if (!variantData) {
          product.quickViewVariants = [];
          return product.quickViewVariants;
        }

        try {
          product.quickViewVariants = JSON.parse(variantData.textContent || '[]');
        } catch (error) {
          product.quickViewVariants = [];
        }

        return product.quickViewVariants;
      }

      ensureQuickViewSwatchSelection(product) {
        product.querySelectorAll('[data-cart-quickview-option-group]').forEach((group) => {
          const swatches = Array.from(group.querySelectorAll('[data-cart-quickview-option-swatch]'));
          if (!swatches.length || swatches.some((swatch) => swatch.classList.contains('is-selected'))) return;

          swatches[0].classList.add('is-selected');
          swatches[0].setAttribute('aria-pressed', 'true');
        });
      }

      getQuickViewSelectedOptions(product) {
        const selectedOptions = [];

        product.querySelectorAll('[data-cart-quickview-option-select]').forEach((select) => {
          const position = Number(select.dataset.optionPosition || 1) - 1;
          selectedOptions[position] = select.value;
        });

        product.querySelectorAll('[data-cart-quickview-option-swatch].is-selected').forEach((swatch) => {
          const position = Number(swatch.dataset.optionPosition || 1) - 1;
          selectedOptions[position] = swatch.dataset.optionValue;
        });

        return selectedOptions;
      }

      quickViewVariantMatchesOptions(variant, selectedOptions) {
        return selectedOptions.every((value, index) => {
          if (!value) return true;
          return String(variant[`option${index + 1}`] || '') === String(value);
        });
      }

      findQuickViewVariant(product) {
        const variants = this.getQuickViewVariants(product);
        const selectedOptions = this.getQuickViewSelectedOptions(product);
        const availableVariant = variants.find((variant) => this.quickViewVariantMatchesOptions(variant, selectedOptions) && variant.available);

        return availableVariant || variants.find((variant) => this.quickViewVariantMatchesOptions(variant, selectedOptions)) || null;
      }

      updateQuickViewProduct(product) {
        if (!product) return;

        this.ensureQuickViewSwatchSelection(product);

        const variant = this.findQuickViewVariant(product);
        const available = Boolean(variant && variant.available);
        const addButton = product.querySelector('[data-cart-quickview-add]');
        const addLabel = addButton?.querySelector('.cart-popup-quickview__add-label');
        const price = product.querySelector('[data-cart-quickview-price]');
        const comparePrice = product.querySelector('[data-cart-quickview-compare-price]');
        const message = product.querySelector('[data-cart-quickview-message]');

        if (addButton) {
          addButton.dataset.variantId = variant?.id || '';
          addButton.dataset.available = available ? 'true' : 'false';
          addButton.disabled = !available;
          addButton.setAttribute('aria-label', available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldOut);
          addButton.title = available ? addButton.dataset.labelAdd : addButton.dataset.labelSoldOut;
        }

        if (addLabel) {
          addLabel.textContent = available ? 'ADD TO CART' : addButton?.dataset.labelSoldOut || 'Sold out';
        }

        if (price && variant?.price) {
          price.textContent = variant.price;
        }

        if (comparePrice) {
          comparePrice.textContent = variant?.comparePrice || '';
          comparePrice.hidden = !variant?.comparePrice;
        }

        if (variant?.image && this.quickViewSwiper) {
          const slides = Array.from(product.querySelectorAll('[data-cart-quickview-gallery-slide]'));
          const imageIndex = slides.findIndex((slide) => slide.dataset.imageSrc === variant.image);
          if (imageIndex >= 0) this.quickViewSwiper.slideTo(imageIndex);
        }

        if (message) {
          message.textContent = '';
          message.classList.remove('is-error');
        }
      }

      selectQuickViewSwatch(button) {
        const group = button.closest('[data-cart-quickview-option-group]');
        const product = button.closest('[data-cart-quickview-product]');
        if (!group || !product) return;

        group.querySelectorAll('[data-cart-quickview-option-swatch]').forEach((swatch) => {
          const isSelected = swatch === button;
          swatch.classList.toggle('is-selected', isSelected);
          swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });

        this.updateQuickViewProduct(product);
      }

      setQuickViewButtonLoading(button, isLoading) {
        button.classList.toggle('loading', isLoading);
        button.toggleAttribute('aria-disabled', isLoading);
        button.disabled = isLoading || button.dataset.available === 'false';

        const spinner = button.querySelector('.loading__spinner');
        if (spinner) spinner.classList.toggle('hidden', !isLoading);
      }

      showQuickViewMessage(product, text, isError) {
        const message = product?.querySelector('[data-cart-quickview-message]');
        if (!message) return;

        message.textContent = text || '';
        message.classList.toggle('is-error', Boolean(isError));
      }

      async addQuickViewToCart(button) {
        if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

        const product = button.closest('[data-cart-quickview-product]');
        const variantId = button.dataset.variantId;
        if (!product || !variantId) return;
        if (!this.startCartMutation([button])) return;

        const formData = new FormData();
        formData.append('id', variantId);
        formData.append('quantity', '1');
        formData.append('sections', this.getSectionsToRender().map((section) => section.id).join(','));
        formData.append('sections_url', window.location.pathname);
        let mutationCooldown = this.cartMutationCooldown;
        let cooldownFromNow = false;

        this.showQuickViewMessage(product, '', false);

        try {
          const config = typeof fetchConfig === 'function'
            ? fetchConfig('javascript')
            : {
                method: 'POST',
                headers: {
                  Accept: 'application/javascript',
                },
              };

          config.headers = config.headers || {};
          delete config.headers['Content-Type'];
          config.headers['X-Requested-With'] = 'XMLHttpRequest';
          config.body = formData;

          const parsedState = await this.fetchCartMutation(this.getCartAddUrl(), config, 'Unable to add item.');

          await this.renderNormalizedContents(parsedState, true);

          if (typeof publish === 'function' && window.PUB_SUB_EVENTS) {
            try {
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'cart-popup-recommendations',
                productVariantId: variantId,
                cartData: parsedState,
              });
            } catch (publishError) {
              console.error(publishError);
            }
          }
        } catch (error) {
          this.showQuickViewMessage(product, error.message || window.cartStrings?.error || 'Unable to add item.', true);
          mutationCooldown = error.status === 429 ? error.retryAfter || this.cartMutationBackoff : this.cartMutationCooldown;
          cooldownFromNow = error.status === 429;
        } finally {
          if (this.cartMutationLocked) this.finishCartMutation([button], mutationCooldown, cooldownFromNow);
        }
      }

      getSectionsToRender() {
        return [
          {
            id: 'cart-popup',
            selector: '#CartPopup',
          },
          {
            id: 'cart-icon-bubble',
          },
        ];
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        const element = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        return element ? element.innerHTML : null;
      }

      setActiveElement(element) {
        this.activeElement = element;
      }
    }
  );
}
